import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { PaginatedBody } from '@/api/types';
import { queryClient } from '@/queries/index';
import { UserPreferenceType, UserProfileType } from '@/api/apis/user';
import { BASE_QUERY_KEYS } from '@/queries/consts';

export const getNextPageParamQueryOption = <C, T extends PaginatedBody<C>>(page: T) => {
  if (page.pageIndex + 1 >= page.totalPages) {
    return undefined;
  }

  return page.pageIndex + 1;
};

export const getPrevPageParamQueryOption = <C, T extends PaginatedBody<C>>(page: T) => {
  if (page.pageIndex <= 1) {
    return undefined;
  }

  return page.pageIndex - 1;
};

export const DEFAULT_INFINITE_QUERY_OPTIONS = {
  initialPageParam: 0,
  getNextPageParam: getNextPageParamQueryOption,
  getPreviousPageParam: getPrevPageParamQueryOption,
};

export const resetAllQueries = (): void => {
  queryClient.resetQueries();
};

export const updateUserPreferencesInCache = (
  previous: UserProfileType,
  updated: UserPreferenceType[],
): UserProfileType => {
  if (!previous) return previous;
  const filtered = previous.userPreferences.filter((existing) => !updated.some((p) => p.name === existing.name));

  return {
    ...previous,
    userPreferences: [...filtered, ...updated],
  };
};

export const generateBaseQueryKeysList = (modelName: string) => ({
  getInfiniteList: (...args: unknown[]) => [modelName, BASE_QUERY_KEYS.infiniteList, ...args] as const,
  getList: (...args: unknown[]) => [modelName, BASE_QUERY_KEYS.list, ...args] as const,
  getItem: (...args: unknown[]) => [modelName, BASE_QUERY_KEYS.item, ...args] as const,
});

type EntityDeleteMutationResult = {
  correlationId?: string;
  message: string | null;
};

type EntityDeleteMutationVariables = {
  id: string;
  type?: string;
};

export const wrapMutationForEntityDelete = <
  TData,
  TError extends Error,
  TVariables extends { id: string; type?: string },
>(
  useMutationHook: () => UseMutationResult<TData, TError, TVariables>,
): (() => UseMutationResult<EntityDeleteMutationResult, Error, EntityDeleteMutationVariables>) => {
  return () => {
    const originalMutation = useMutationHook();

    return useMutation<EntityDeleteMutationResult, Error, EntityDeleteMutationVariables>({
      mutationFn: async (variables: EntityDeleteMutationVariables) => {
        try {
          await originalMutation.mutateAsync(variables as TVariables);

          return {
            correlationId: undefined,
            message: null,
          };
        } catch (e) {
          const error = e as Error & { faultedMessage?: string };

          if (error.faultedMessage) {
            throw new Error(error.faultedMessage);
          }

          throw error;
        }
      },
    });
  };
};
